import { memo, useCallback, useMemo } from 'react';
import UITextInput from 'UIComponents/input/UITextInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
// @ts-expect-error Untyped dependency
import PhoneNumberInput from 'ui-addon-i18n/components/PhoneNumberInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIIcon from 'UIComponents/icon/UIIcon';
import styled from 'styled-components';
import { isValidRawPhoneNumber, parseRawPhoneNumber } from './phone/utils/phoneNumberUtils';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { FETCH_STATUS } from './phone/hooks/usePhoneNumberValidator';
import { INLINE_ICON_SPACING_X } from 'HubStyleTokens/sizes';
import { SLINKY } from 'HubStyleTokens/colors';
import UITile from 'UIComponents/tile/UITile';
import UITileSection from 'UIComponents/tile/UITileSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Wrapper = styled.div.withConfig({
  displayName: "PropertyInputPhone__Wrapper",
  componentId: "sc-1cuqy6w-0"
})(["min-width:359px;"]);
const StyledUITileSection = styled(UITileSection).withConfig({
  displayName: "PropertyInputPhone__StyledUITileSection",
  componentId: "sc-1cuqy6w-1"
})(["padding:14px 10px 10px 10px;"]);
const StyledUIIcon = styled(UIIcon).withConfig({
  displayName: "PropertyInputPhone__StyledUIIcon",
  componentId: "sc-1cuqy6w-2"
})(["padding-left:", ";"], INLINE_ICON_SPACING_X);
export const getPhoneNumberString = (phoneNumber, extension) => extension !== null && extension !== '' && extension !== undefined ? `${phoneNumber} ext ${extension}` : phoneNumber || '';
const PropertyInputPhone = ({
  disabled,
  value,
  initialCountryDialCode,
  status,
  error,
  showTextInput,
  onChange,
  onToggleInput
}) => {
  const {
    rawValue,
    extensionValue
  } = useMemo(() => {
    if (isValidRawPhoneNumber(value)) {
      const {
        phoneNumber,
        extension
      } = parseRawPhoneNumber(value);
      return {
        rawValue: phoneNumber,
        extensionValue: extension
      };
    }
    return {
      rawValue: value,
      extensionValue: ''
    };
  }, [value]);
  const handleTextChange = useCallback(evt => {
    onChange(evt.target.value);
  }, [onChange]);
  const handlePhoneChange = useCallback(({
    phoneNumber,
    extension,
    country
  }) => {
    const newPhoneNumber = getPhoneNumberString(phoneNumber, extension);
    onChange(newPhoneNumber, country);
  }, [onChange]);
  const renderErrorMessage = useMemo(() => {
    const isPending = status === FETCH_STATUS.PENDING;
    const isSucceeded = status === FETCH_STATUS.SUCCEEDED;
    if (value === null || value === undefined || value === '' || value === initialCountryDialCode) {
      return /*#__PURE__*/_jsx("span", {
        style: {
          color: SLINKY
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.prevalidation",
          "data-unit-test": "prevalidation-message"
        })
      });
    }
    if ((isPending || isSucceeded) && error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.numberInvalid",
        "data-unit-test": "error-number-invalid"
      });
    }
    if (error) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberErrors.validationFailed"
      });
    }
    if (isPending) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validating"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPhone.phoneNumberValidations.validated"
    });
  }, [status, error, value, initialCountryDialCode]);
  const message = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.btn' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.btn';
  const tooltip = showTextInput ? 'customerDataProperties.PropertyInputPhone.editOptions.editAsPhoneNumber.tooltip' : 'customerDataProperties.PropertyInputPhone.editOptions.editAsText.tooltip';
  return /*#__PURE__*/_jsxs(Wrapper, {
    children: [showTextInput ? /*#__PURE__*/_jsx(UITextInput, {
      disabled: disabled,
      value: value,
      onChange: handleTextChange,
      autoFocus: true
    }) : /*#__PURE__*/_jsx(UITile, {
      compact: true,
      children: /*#__PURE__*/_jsx(StyledUITileSection, {
        children: /*#__PURE__*/_jsx(UIFormControl, {
          error: error,
          validationMessage: renderErrorMessage,
          "aria-label": unescapedText('customerDataProperties.PropertyInputPhone.ariaLabel'),
          children: /*#__PURE__*/_jsx("div", {
            className: "display-flex",
            children: /*#__PURE__*/_jsx(PhoneNumberInput, {
              value: rawValue || initialCountryDialCode,
              extensionValue: extensionValue,
              phoneInputProps: {
                disabled,
                autoFocus: true
              },
              onChange: handlePhoneChange,
              countrySelectProps: {
                disabled
              },
              extensionInputProps: {
                disabled
              }
            })
          })
        })
      })
    }), /*#__PURE__*/_jsxs(UIButton, {
      className: "p-top-3",
      use: "link",
      onClick: onToggleInput,
      "data-selenium-test": "toggle-input-type-button",
      disabled: status === FETCH_STATUS.PENDING,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: message
      }), /*#__PURE__*/_jsx(UITooltip, {
        placement: "bottom",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: tooltip
        }),
        children: /*#__PURE__*/_jsx(StyledUIIcon, {
          name: "info"
        })
      })]
    })]
  });
};
export default /*#__PURE__*/memo(PropertyInputPhone);